import { Link } from 'react-router-dom';
import type { ArrowUpCircle } from 'lucide-react';
import { ArrowDownCircle, CheckCircle2, Clock, ChevronRight, Landmark } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { InstructionTrigger } from '@/features/instructions/InstructionDrawer';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { REPORT_DESCRIPTIONS, REPORT_LABELS } from './types';

interface CardConfig {
  to: string;
  label: string;
  description: string;
  icon: typeof ArrowUpCircle;
  accent: string;
  iconBg: string;
}

const CARDS: CardConfig[] = [
  {
    to: '/app/relatorios/payable',
    label: REPORT_LABELS.payable,
    description: REPORT_DESCRIPTIONS.payable,
    icon: Clock,
    accent: 'text-expense',
    iconBg: 'bg-expense/10',
  },
  {
    to: '/app/relatorios/paid',
    label: REPORT_LABELS.paid,
    description: REPORT_DESCRIPTIONS.paid,
    icon: ArrowDownCircle,
    accent: 'text-expense',
    iconBg: 'bg-expense/10',
  },
  {
    to: '/app/relatorios/receivable',
    label: REPORT_LABELS.receivable,
    description: REPORT_DESCRIPTIONS.receivable,
    icon: Clock,
    accent: 'text-income',
    iconBg: 'bg-income/10',
  },
  {
    to: '/app/relatorios/received',
    label: REPORT_LABELS.received,
    description: REPORT_DESCRIPTIONS.received,
    icon: CheckCircle2,
    accent: 'text-income',
    iconBg: 'bg-income/10',
  },
  {
    to: '/app/relatorios/extrato',
    label: 'Extrato da Conta',
    description:
      'Lançamentos realizados de uma conta no período, com saldo linha a linha e transferências.',
    icon: Landmark,
    accent: 'text-primary',
    iconBg: 'bg-primary/10',
  },
];

export function ReportsPage() {
  const { clientId } = useCurrentClient();

  if (!clientId) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Selecione um cliente para gerar relatórios.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Relatórios</h1>
          <p className="text-sm text-muted-foreground">
            Geração de relatórios filtrados por período, conta, fornecedor ou cliente.
          </p>
        </div>
        <InstructionTrigger contextKey="generate-report" variant="outline" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {CARDS.map(({ to, label, description, icon: Icon, accent, iconBg }) => (
          <Link key={to} to={to}>
            <Card className="group transition-shadow hover:shadow-md">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className={`rounded-lg p-3 ${iconBg}`}>
                    <Icon className={`h-6 w-6 ${accent}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-foreground">{label}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                  </div>
                  <ChevronRight
                    size={18}
                    className="mt-3 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5"
                  />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-sm text-muted-foreground">
        <p>
          <strong>Dica:</strong> os relatórios usam contraparte (fornecedor/cliente) para o filtro
          principal. Cadastre fornecedores e clientes em{' '}
          <Link to="/app/contrapartes" className="text-primary hover:underline">
            Cadastros → Fornecedores/Clientes
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
